import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Star, Flame } from 'lucide-react';

export const ProductCard = ({ product, showBestseller = false }) => {
  const { addToCart, updateQuantity, cart } = useCart();
  const navigate = useNavigate();

  // Get quantity in cart for this product
  const cartItem = cart.find(item => item.id === product.id);
  const quantityInCart = cartItem?.quantity || 0;

  // Calculate savings
  const savingsAmount = product.original_price ? product.original_price - product.price : 0;
  const savingsPercent = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
  
  // Simulated sold count (consistent per product using id hash)
  const soldCount = product.id ? (parseInt(product.id.slice(-4), 16) % 300) + 150 : 200;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.title} added to cart!`);
    
    // Track Facebook Pixel AddToCart event
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_ids: [product.id],
        content_type: 'product',
        content_name: product.title,
        content_category: product.category,
        value: product.price,
        currency: 'INR',
      });
    }
  };

  const handleIncrement = (e) => {
    e.stopPropagation();
    addToCart(product);
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    updateQuantity(product.id, quantityInCart - 1);
  };

  const handleClick = () => {
    navigate(`/products/${product.id}`);
  };

  return (
    <Card
      className="overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
      onClick={handleClick}
      data-testid="product-card"
    >
      <CardContent className="p-0">
        <div className="relative">
          <AspectRatio ratio={4/3}>
            <img
              src={product.image_urls[0] || 'https://via.placeholder.com/300'}
              alt={product.title}
              className="object-cover w-full h-full rounded-t-xl"
            />
          </AspectRatio>
          {/* Top Badges Row */}
          <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
            {/* Left badges */}
            <div className="flex flex-col gap-1">
              {showBestseller && (
                <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 text-[10px] px-1.5 py-0.5">
                  <Flame className="w-3 h-3 mr-0.5" />
                  Bestseller
                </Badge>
              )}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border border-[hsl(32_95%_44%)] bg-[hsl(43_96%_92%)] text-[hsl(222_47%_11%)] whitespace-nowrap">
                🚚 FREE
              </span>
            </div>
            {/* Discount badge - Right */}
            {savingsPercent > 0 && (
              <Badge className="bg-green-500 text-white border-0 text-[10px] px-1.5 py-0.5 font-bold">
                {savingsPercent}% OFF
              </Badge>
            )}
          </div>
          
          {/* Low Stock Badge - Bottom Left */}
          {product.stock && product.stock <= 5 && product.stock > 0 && (
            <div className="absolute bottom-2 left-2">
              <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0.5 animate-pulse">
                Only {product.stock} left!
              </Badge>
            </div>
          )}
        </div>
        <div className="p-3 space-y-2">
          {/* Rating & Sold */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold text-gray-700">{product.avg_rating?.toFixed(1) || '4.8'}</span>
              <span className="text-gray-400">({product.review_count || Math.floor(soldCount/3)})</span>
            </div>
            <span className="text-gray-500">{soldCount}+ sold</span>
          </div>
          
          {/* Title */}
          <h3 className="font-semibold text-sm line-clamp-2 min-h-[2.5rem]">{product.title}</h3>
          
          {/* Price Section */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-1.5">
                <p className="text-lg font-extrabold tracking-tight" data-testid="product-card-price">
                  ₹{product.price}
                </p>
                {product.original_price && (
                  <p className="text-xs text-gray-400 line-through">
                    ₹{product.original_price}
                  </p>
                )}
              </div>
              {savingsAmount > 0 && (
                <p className="text-[10px] text-green-600 font-semibold">
                  Save ₹{savingsAmount}
                </p>
              )}
            </div>
            
            {/* Show +/- when item is in cart, otherwise show Add to Cart */}
            {quantityInCart > 0 ? (
              <div className="flex items-center gap-1 bg-primary rounded-xl" onClick={e => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-white hover:bg-primary/80 rounded-l-xl"
                  onClick={handleDecrement}
                  data-testid="product-card-decrement"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-6 text-center text-white font-semibold text-sm">
                  {quantityInCart}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-white hover:bg-primary/80 rounded-r-xl"
                  onClick={handleIncrement}
                  data-testid="product-card-increment"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                className={`rounded-xl text-white shadow-md ${
                  product.in_stock && (product.stock_count === undefined || product.stock_count > 0)
                    ? 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={handleAddToCart}
                disabled={!product.in_stock || (product.stock_count !== undefined && product.stock_count <= 0)}
                data-testid="product-card-add-to-cart-button"
              >
                {product.in_stock && (product.stock_count === undefined || product.stock_count > 0) ? 'Add' : 'Sold Out'}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};